import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { seoArticles } from '@/mocks/categories';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuy.cam';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead
          title="Article Not Found – OOPBuy Spreadsheet"
          description="The article you are looking for does not exist. Browse our shopping guides and product resources."
          noIndex={true}
        />
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-4-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = seoArticles.filter((a) => a.slug !== slug).slice(0, 3);
  const articleUrl = `${SITE_URL}/article/${article.slug}`;

  return (
    <>
      <SEOHead
        title={`${article.title} – OOPBuy Spreadsheet`}
        description={article.description}
        keywords={`oopbuy spreadsheet, ${article.category.toLowerCase()} shopping guide, spreadsheet shopping list, ${article.category.toLowerCase()} deals 2026`}
        canonicalUrl={articleUrl}
        ogTitle={article.title}
        ogDescription={article.description}
        ogUrl={articleUrl}
        ogImage={article.image}
        ogType="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.description,
          image: article.image,
          datePublished: '2026-06-01',
          dateModified: '2026-06-21',
          author: {
            '@type': 'Organization',
            name: 'OOPBuy Spreadsheet',
            url: SITE_URL,
          },
          publisher: {
            '@type': 'Organization',
            name: 'OOPBuy Spreadsheet',
            url: SITE_URL,
          },
          url: articleUrl,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': articleUrl,
          },
        }}
      />
      <Navbar />
      <main>
        <article>
          {/* ... existing code ... */}
          <header className="relative pt-24 pb-12 md:pt-28 md:pb-20 bg-background-50 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">Updated: June 2026</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
                {article.description}
              </p>
            </div>
          </header>

          <div className="w-full max-w-4xl mx-auto px-4 md:px-0 mb-10 md:mb-16">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover aspect-[16/9]"
              />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 pb-10 md:pb-16">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-8">
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Discover the Best Deals with the OOPBuy Spreadsheet
                </h2>
                <p className="mb-4">
                  The <strong>OOPBuy spreadsheet</strong> has transformed how savvy shoppers approach online purchasing. Rather than spending hours jumping between different retail websites and comparing prices manually, the spreadsheet consolidates everything into one streamlined, searchable database. Each product listing includes up-to-date pricing, community ratings, and real-time availability status.
                </p>
                <p className="mb-4">
                  What makes the <strong>OOPBuy spreadsheet</strong> particularly powerful is its structured format. The spreadsheet approach allows instant filtering by category, price range, and rating. You can sort products by popularity to spot trending items or by price to identify exceptional deals. This level of organization is something traditional online stores simply cannot offer.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {article.category} Shopping with the OOPBuy Spreadsheet
                </h2>
                {article.content.split('\n').filter(Boolean).map((paragraph, idx) => (
                  <p key={idx} className="mb-4">{paragraph}</p>
                ))}
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  How the Spreadsheet Format Saves You Time and Money
                </h2>
                <p className="mb-4">
                  Traditional online shopping involves opening multiple browser tabs, manually tracking prices across different stores, and hoping you do not miss a better deal elsewhere. The <strong>OOPBuy spreadsheet</strong> eliminates this inefficiency entirely. By presenting all available options in a single organized view, you can scan hundreds of products in the time it would normally take to browse just a handful of individual product pages.
                </p>
                <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-6">
                  <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-4">Key Advantages of the Spreadsheet Approach</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 mt-0.5">
                        <i className="ri-check-line text-xs text-primary-600"></i>
                      </span>
                      <span className="text-sm">Instant side-by-side price comparison across all available products</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 mt-0.5">
                        <i className="ri-check-line text-xs text-primary-600"></i>
                      </span>
                      <span className="text-sm">Community-driven ratings help identify the highest quality products</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 mt-0.5">
                        <i className="ri-check-line text-xs text-primary-600"></i>
                      </span>
                      <span className="text-sm">Weekly updates ensure listings stay current and sold-out items are removed</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 mt-0.5">
                        <i className="ri-check-line text-xs text-primary-600"></i>
                      </span>
                      <span className="text-sm">Sortable columns let you filter by what matters most to you — price, rating, or availability</span>
                    </li>
                  </ul>
                </div>
                <p className="mb-4">
                  The <strong>OOPBuy guide</strong> approach focuses on empowering shoppers with transparent information. You see exactly what is available, at what price, with what community rating — all at once. This transparency builds trust and enables confident purchasing decisions.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Start Shopping Smarter Today
                </h2>
                <p className="mb-4">
                  The <strong>OOPBuy spreadsheet</strong> represents the next evolution in online shopping. By combining the organizational power of spreadsheets with the discovery potential of curated product databases, it delivers an experience that traditional e-commerce cannot match. Whether you are a casual shopper looking for everyday essentials or a dedicated fashion enthusiast hunting for rare finds, the spreadsheet format adapts to your needs.
                </p>
                <p className="mb-6">
                  Weekly updates ensure you never miss new arrivals or restocks in your favorite categories. The community-driven rating system surfaces hidden gems that might otherwise go unnoticed. And the price comparison capability means you always know you are getting the best possible deal.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Access Full Spreadsheet
                    <i className="ri-arrow-right-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold text-foreground-800 border-2 border-foreground-200 rounded-full hover:border-foreground-400 transition-colors whitespace-nowrap"
                  >
                    Back to Home
                    <i className="ri-home-4-line"></i>
                  </Link>
                </div>
              </section>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
              <div className="border-t border-background-200 pt-12 md:pt-16">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
                  Explore More Shopping Guides
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group flex flex-col bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer overflow-hidden"
                    >
                      <div className="w-full aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                            {rel.category}
                          </span>
                          <span className="text-xs text-foreground-500">{rel.readTime}</span>
                        </div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                          {rel.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}